import { Component, OnInit } from "@angular/core";
import { ActionResult } from "../../../model/action-result";
import { MultiActionService } from "../../../services/multi-action.service";
import { Messages } from "../../../model/messages";
import { BehaviorSubject } from "rxjs";
import { Ticket } from "../../../model/ticket";
import { TicketService } from "../../../services/ticket.service";
import { AuthService } from "../../../auth.service";
import { Router } from "@angular/router";
import { NavigationHelper } from "../../../services/navigation.helper";
import { StatusService } from "../../../services/status.service";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "processing",
  templateUrl: "./processing.component.html",
  styleUrls: ["./processing.component.css"],
})
export class ProcessingComponent implements OnInit {
  actionResults: ActionResult[] = [];
  messages = new BehaviorSubject<Messages>(Messages.EMPTY);

  constructor(
    public multiAction: MultiActionService,
    private ticketService: TicketService,
    private auth: AuthService,
    private router: Router,
    private nav: NavigationHelper,
    private status: StatusService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.multiAction.actionResults.subscribe((results) => {
      this.actionResults = results;
    });

    this.multiAction.processingCompleted.subscribe(() => {
      const token = this.auth.token;
      this.ticketService.search(token).subscribe((messages) => {
        const updatedTicket = this.ticketService.ticketData;
        this.ticketService._currentTicket.next(updatedTicket);
      });
    });

    this.multiAction.processTickets();
  }

  isSuccess(result: ActionResult): boolean {
    return result.messages.empty;
  }

  /**
   * The opening of a ticket's description, as one line of plain text.
   *
   * The description is html - the detail screen renders it through a
   * sanitizer - so the tags come out here rather than going in: a paragraph or
   * a list would break the single line this is meant to be. Whitespace is
   * collapsed for the same reason.
   *
   * Cut at 100 characters, and the css trims it again to whatever the panel is
   * actually wide enough for. Two limits rather than one, because the first
   * keeps the dom small and the second keeps the line honest at any width.
   */
  summary(ticket: Ticket): string {
    const text = (ticket?.description ?? "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    return text.length > 100 ? text.slice(0, 100).trimEnd() + "\u2026" : text;
  }

  /** Why a ticket was not changed, as message codes. */
  problems(result: ActionResult): string[] {
    return Array.from(result.messages.all.values()).flat();
  }
  /**
   * What the run just did, in one sentence beside the close button.
   *
   * Read from the action that was chosen rather than from the tickets: the
   * tickets have already been updated by the time this is drawn, so they no
   * longer say what changed about them, and 'clear' has deleted them outright.
   * The action is still on the service and still knows.
   *
   * The count is of the tickets that actually succeeded, not of the ones that
   * were selected, so the sentence cannot claim work that failed.
   */
  get changeSummary(): string {
    const count = this.actionResults.filter((one) => this.isSuccess(one)).length;

    if (count < 1) {
      return "";
    }
    // one and many are separate keys: there is no message-format compiler here,
    // so "1 tickets zijn" is what a single {{count}} would give
    const tickets = this.translate.instant(
      count === 1 ? "process.result.ticketOne" : "process.result.ticketMany",
      { count }
    );
    const action = this.multiAction.selectedAction.value;

    switch (action?.name?.toLowerCase()) {
      // 'verwijderen' archives as well, so it says so: the sentence describes
      // what happened, not what the button on the previous screen was called
      case "action.archive":
      case "action.clear":
        return this.translate.instant("process.result.archived", { tickets });
      case "action.activate":
        return this.translate.instant("process.result.activated", { tickets });
      default:
        break;
    }
    const statusId = this.multiAction.statusIdForActionId(action?.id ?? 0);
    const status = statusId ? this.status.labelForId(statusId) : "";

    return status
      ? this.translate.instant("process.result.changed", { tickets, status })
      : this.translate.instant("process.result.processed", { tickets });
  }

  get allTicketsSuccessful(): boolean {
    return (
      this.actionResults.length > 0 &&
      this.actionResults.every((result) => this.isSuccess(result))
    );
  }
  finish() {
    const pageId = this.ticketService.currentPage.value;

    this.router
      .navigate(["/tickets/-1", "result", "page", pageId])
      .then((success) => {});
  }
}
